import React from "react";
import "../../styles/TrustPages.css";

/* The circular Trust Score gauge from Tom's Trusted by Percy mocks.
   One dial, one number, one band label — the band wording is fixed
   site-wide (Building / Good / Excellent) so every page agrees.

   ⚠ The thresholds below MUST match api/profiles/trust_score.py
   (BAND_GOOD_MIN / BAND_EXCELLENT_MIN). Two places, one meaning.

   ⚠ ILLUSTRATIVE_SCORE is for the marketing pages only, where the dial
   sits on an invented example company ("Apex Plumbing"). Anywhere a REAL
   contractor is shown, pass their real score — a hardcoded number on a
   real profile is a claim we can't back.

   All text is drawn inside the SVG's 100x100 viewBox, so the label, score
   and band scale with whatever box the caller gives the dial (132px card,
   190px page, 240px hero) instead of being sized in fixed pixels that only
   fit the largest dial. */

const CIRCUMFERENCE = 2 * Math.PI * 44; // r=44 in a 100x100 viewBox

export const ILLUSTRATIVE_SCORE = 88;

export const bandForScore = (score) => {
  if (score >= 75) return "Excellent";
  if (score >= 50) return "Good";
  return "Building";
};

const BAND_CHIP_WIDTH = { Excellent: 40, Good: 27, Building: 37 };

const TrustScoreDial = ({ score = ILLUSTRATIVE_SCORE, className = "" }) => {
  const clamped = Math.max(0, Math.min(100, score));
  const filled = (clamped / 100) * CIRCUMFERENCE;
  const band = bandForScore(clamped);
  const chipWidth = BAND_CHIP_WIDTH[band];

  return (
    <div
      className={`tp-dial relative bg-white rounded-full border border-gray-100 flex items-center justify-center p-4 ${className}`}
      role="img"
      aria-label={`Trust Score ${clamped} out of 100 — ${band}`}
    >
      <svg viewBox="0 0 100 100" className="w-full h-full" aria-hidden="true">
        <g transform="rotate(-90 50 50)">
          <circle
            cx="50"
            cy="50"
            r="44"
            fill="none"
            stroke="#d1d5db"
            strokeWidth="8"
            className="tp-dial-track"
          />
          <circle
            cx="50"
            cy="50"
            r="44"
            fill="none"
            stroke="#10b981"
            strokeWidth="8"
            strokeLinecap="round"
            className="tp-dial-fill"
            style={{
              strokeDasharray: `${filled} ${CIRCUMFERENCE}`,
              "--tp-dial-filled": filled,
              "--tp-dial-circumference": CIRCUMFERENCE,
            }}
          />
        </g>
        <text
          x="50"
          y="31"
          textAnchor="middle"
          fontSize="6"
          fontWeight="700"
          letterSpacing="0.6"
          fill="currentColor"
          className="tp-dial-label text-gray-400"
        >
          TRUST SCORE
        </text>
        <text
          x="50"
          y="59.5"
          textAnchor="middle"
          fontSize="27"
          fontWeight="900"
          letterSpacing="-1"
          fill="currentColor"
          className="tp-dial-score text-gray-900"
        >
          {clamped}
        </text>
        <rect
          x={50 - chipWidth / 2}
          y="66"
          width={chipWidth}
          height="10.5"
          rx="5.25"
          fill="#f0fdf4"
          stroke="#dcfce7"
          strokeWidth="0.5"
          className="tp-dial-band-rect"
        />
        <text
          x="50"
          y="73.4"
          textAnchor="middle"
          fontSize="6"
          fontWeight="700"
          letterSpacing="0.15"
          fill="#10b981"
          className="tp-dial-band"
        >
          {band}
        </text>
      </svg>
      <div className="absolute inset-0 rounded-full tp-dial-pulse pointer-events-none"></div>
    </div>
  );
};

export default TrustScoreDial;
